import { first, map } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Company } from 'src/app/common/_models';
import { AppState } from 'src/app/common/_reducers';
import { selectAllCompanies } from 'src/app/common/_selectors/company.selectors';

@Injectable()
export class CompanyService {
  private apiBaseUrl = environment.apiBaseUrl;

  companyList$: Observable<Company[]>;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) {
    this.companyList$ = this.store.pipe(select(selectAllCompanies));
  }

  getFromLocalStore(): Observable<Company[]> {
    return this.companyList$;
  }

  getCompanies(allMultifarm = false, excludes = []): Observable<Company[]> {
    let excludesList = [];
    if (excludes && excludes.length > 0) {
      excludesList = excludes;
    } else {
      excludesList = [
        'employees',
        'suppliers',
        'customers',
        'certifications',
        'securities',
        'contacts',
        'insurances',
      ];
    }
    let excludesAppend = '';
    excludesList.forEach((item) => {
      excludesAppend += '&excludes=' + item;
    });
    let all = '';
    if (allMultifarm) {
      all = '&all=' + allMultifarm;
    }

    return this.http.get<Company[]>(
      this.apiBaseUrl + 'api/private/v1/companies?type=farm' + all + excludesAppend
    );
  }

  /**
   * Get the Company object of a company in a multifarm. Do it by passing its IDs.
   * @param {Number} id This is the ID (or the companyID) that you can get in the Farm object.
   * @param {number} requestingCompanyId The ID of the company (self, or child company) that is doing the request: company.id in the Farm object.
   * @param {string[]} excludes An array of properties to exclude from the response.
   * @returns {Observable<Company>} An Observable of the Company object.
   */
  getMultiFarmCompanyById(
    id: number,
    requestingCompanyId: number,
    excludes: string[] = []
  ): Observable<Company> {
    let excludesList = [];
    if (excludes && excludes.length > 0) {
      excludesList = excludes;
    } else {
      excludesList = [
        'employees',
        'suppliers',
        'customers',
        'certifications',
        'securities',
        'contacts',
        'insurances',
      ];
    }
    let excludesAppend = '';
    excludesList.forEach((item) => {
      excludesAppend += '&excludes=' + item;
    });

    return this.http
      .get<Company>(
        this.apiBaseUrl +
          `api/private/v1/companies/${id}/?companyIds=${requestingCompanyId}` +
          excludesAppend
      )
      .pipe(map((response) => response));
  }

  saveCompany(companyItem): Observable<Company> {
    const company = { ...companyItem };
    delete company.lastUpdated;
    delete company.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    company.companyId = companySelected ? companySelected.id : null;

    if (company.user != null && company.user.id != null) {
      company.user = { id: company.user.id };
    }
    if (company.id) {
      return this.http
        .put<Company>(this.apiBaseUrl + 'api/private/v1/companies/' + company.id, company)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Company>(this.apiBaseUrl + 'api/private/v1/companies/', company)
        .pipe(map((response) => response));
    }
  }

  deleteCompany(id: number): Observable<Company> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/companies/' + id).pipe(
      map(() => {
        return { id } as Company;
      })
    );
  }

  getAllQualifications = () => {
    return [
      'storage',
      'transformator',
      'agricultural_producer',
      'industrial_manufacturer',
      'retailer',
      'distributor',
      'importer',
      'zootechnical_producer',
      'other',
    ];
  };

  getCompanyTypeFarmBycurrentFarm = (currentFarm: Company): Company[] => {
    let companyList: Array<Company>;
    this.companyList$
      .pipe(first((list: Company[]) => list && list.length > 0))
      .subscribe((list: Company[]) => {
        companyList = list.filter((f) => f.companyId === currentFarm.id && f.type === 'farm');
      });
    return companyList;
  };
}
